import { Stack, router } from 'expo-router';
import { BellIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native';

import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import {
  cancelLocalEventReminder,
  fetchMyUpcomingReminders,
  type UpcomingReminder,
} from '@/lib/notifications';

function formatWhen(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleString([], {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    weekday: 'short',
  });
}

/**
 * The member's own reminders — the alerts they set for runs they are going to.
 * Club-wide messaging lives in the admin area; this screen is deliberately just
 * "what my phone will tell me".
 */
export default function MemberNotificationsScreen() {
  const [reminders, setReminders] = React.useState<UpcomingReminder[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Bumped after cancelling so the list reloads. This screen only renders inside
  // the authenticated group, so there is always a session to read reminders for.
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    let active = true;

    async function load() {
      try {
        const data = await fetchMyUpcomingReminders();
        if (active) {
          setReminders(data);
          setError(null);
        }
      } catch (loadError) {
        if (active) {
          setError(
            loadError instanceof Error ? loadError.message : 'Could not load notifications.'
          );
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [refreshKey]);

  async function cancel(reminder: UpcomingReminder) {
    try {
      await cancelLocalEventReminder(reminder);
      setRefreshKey((key) => key + 1);
    } catch (cancelError) {
      setError(cancelError instanceof Error ? cancelError.message : 'Could not cancel reminder.');
    }
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: true, title: 'Notifications' }} />
      <ScrollView
        className="flex-1 bg-background"
        contentContainerClassName="gap-4 p-6"
        contentInsetAdjustmentBehavior="automatic"
      >
        <View className="gap-1">
          <Text variant="large">Your reminders</Text>
          <Text variant="muted">Alerts you set for runs you are going to.</Text>
        </View>

        {isLoading ? <ActivityIndicator /> : null}

        {error ? (
          <Text className="text-sm text-destructive" selectable>
            {error}
          </Text>
        ) : null}

        {!isLoading && !error && reminders.length === 0 ? (
          <View className="items-center gap-3 rounded-md border border-border p-6">
            <Icon as={BellIcon} className="size-6 text-muted-foreground" />
            <Text variant="muted" className="text-center">
              No reminders yet. Open a run you are going to and choose when to be reminded.
            </Text>
          </View>
        ) : null}

        {reminders.map((reminder) => (
          <View className="gap-3 rounded-md border border-border p-4" key={reminder.id}>
            <Pressable
              accessibilityRole="button"
              className="gap-1 active:opacity-70"
              onPress={() =>
                reminder.event
                  ? router.push({
                      pathname: '/app/events/[id]',
                      params: { id: reminder.event.id },
                    })
                  : undefined
              }
            >
              <Text className="font-bold" numberOfLines={2}>
                {reminder.event?.title ?? 'Run'}
              </Text>
              {reminder.event?.place_name ? (
                <Text variant="muted">{reminder.event.place_name}</Text>
              ) : null}
              <Text variant="mono">Notifies {formatWhen(reminder.remind_at)}</Text>
            </Pressable>

            <Pressable
              accessibilityLabel={`Cancel reminder for ${reminder.event?.title ?? 'this run'}`}
              accessibilityRole="button"
              className="self-start active:opacity-70"
              onPress={() => void cancel(reminder)}
            >
              <Text className="font-bold text-destructive">Cancel reminder</Text>
            </Pressable>
          </View>
        ))}
      </ScrollView>
    </>
  );
}
